import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel } from "@mui/material";
import { LoaderCircle } from "lucide-react";
import "./styles.scss";
import NoData from "../../lib/components/Nodata";

export default function DataTable({
  columns,
  rows,
  isLoading,
  onRowClick,
  highlightedRowIndex,
  sortConfig,
  onSortToggle,
  maxHeight = "80vh",
}) {
  return (
    <Paper className="data-table">
      <TableContainer className="data-table__container">
        <div
          className="data-table__wrapper"
          style={{ maxHeight, position: "relative" }} // Add position: relative
        >
          {/* Loader outside the table */}
          {isLoading && (
            <div className="data-table__loader">
              <LoaderCircle className="data-table__loader-icon" size={40} />
            </div>
          )}

          <Table size="small" className="data-table__table" aria-label="Reusable data table">
            {/* Column widths */}
            <colgroup>
              {columns.map((col, idx) => (
                <col key={idx} style={{ width: col.width }} />
              ))}
            </colgroup>

            {/* Header */}
            <TableHead className="data-table__head">
              <TableRow className="data-table__head-row">
                {columns.map((col, idx) => (
                  <TableCell key={idx} className="data-table__head-cell">
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortConfig?.key === col.key}
                        direction={sortConfig?.order || "asc"}
                        onClick={() => onSortToggle?.(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* Body */}
            <TableBody className="data-table__body">
              {!isLoading && rows.length <= 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="data-table__no-data" align="center">
                    <NoData height={"45vh"} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                rows.length > 0 &&
                rows.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    hover
                    onClick={() => onRowClick?.(row, rowIndex)}
                    className={`data-table__row ${
                      highlightedRowIndex === rowIndex ? "data-table__row--highlighted" : ""
                    }`}
                  >
                    {columns.map((col, colIndex) => (
                      <TableCell key={colIndex} className="data-table__cell">
                        {col.render ? col.render(row, rowIndex) : row[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </TableContainer>
    </Paper>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onRowClick: PropTypes.func,
  highlightedRowIndex: PropTypes.number,
  sortConfig: PropTypes.object,
  onSortToggle: PropTypes.func,
  maxHeight: PropTypes.string,
};
